// src/lib/storage/LocalDatabase.ts

import Dexie, {Table} from 'dexie';

import {KVStoreItem, SyncQueueItem,ZSetItem} from './types';

/**
 * LocalDatabase extends Dexie to provide persistent storage using IndexedDB
 * It manages three tables:
 * - kvStore: For basic key-value storage
 * - zsetStore: For sorted sets
 * - syncQueue: For tracking operations that need to be synced to the remote store
 */
export class LocalDatabase extends Dexie {
    // Table definitions
    kvStore!: Table<KVStoreItem, string>;
    zsetStore!: Table<ZSetItem, number>;
    syncQueue!: Table<SyncQueueItem, number>;
    APP_VERSION = '3';

    constructor() {
        super('kv-store');
        this.initializeSchema();
    }

    /**
     * Initializes the database schema with appropriate indexes
     */
    private initializeSchema(): void {
        // Check stored version against current version
        this.checkVersion().catch(console.error);

        this.version(1).stores({
            // key is the primary key, timestamp is indexed for cleanup operations
            kvStore: 'key, timestamp',

            // compound primary key [setKey+member], with individual indexes for queries
            zsetStore: '++id, setKey, memberKey, score, timestamp, [setKey+memberKey]',

            // auto-incrementing id, status index for finding pending items
            syncQueue: '++id, status, timestamp'
        });
    }

    private async checkVersion() {
        try {
            const storedVersion = localStorage.getItem('app_version');
            const currentVersion = this.APP_VERSION;

            if (storedVersion !== currentVersion) {
                console.log('Schema version changed, clearing database...');
                // Close any open connections
                this.close();
                // Delete the database
                await Dexie.delete('kv-store');
                // Store new version
                localStorage.setItem('app_version', currentVersion);
                // Reload the page to reinitialize everything
                window.location.reload();
            }
        } catch (error) {
            console.error('Error checking version:', error);
        }
    }

    /**
     * Clears all data from the database
     */
    async clearAllData(): Promise<void> {
        await this.transaction('rw',
            this.kvStore,
            this.zsetStore,
            this.syncQueue,
            async () => {
                await this.kvStore.clear();
                await this.zsetStore.clear();
                await this.syncQueue.clear();
            }
        );
    }

    /**
     * Clears all local data while preserving the sync queue
     * Use this method to reset local data during development
     */
    async clearLocalData(): Promise<void> {
        await this.transaction('rw',
            this.kvStore,
            this.zsetStore,
            async () => {
                await Promise.all([
                    this.kvStore.clear(),
                    this.zsetStore.clear()
                ]);
            }
        );
    }

    /**
     * Cleans up old sync queue items that are no longer needed
     * @param maxAge Maximum age in milliseconds for sync queue items
     */
    async cleanupSyncQueue(maxAge: number): Promise<void> {
        const cutoff = Date.now() - maxAge;
        await this.syncQueue
            .where('timestamp')
            .below(cutoff)
            .and(item => item.status === 'failed')
            .delete();
    }

    /**
     * Removes stale entries from the KV store
     * @param maxAge Maximum age in milliseconds for KV store items
     */
    async cleanupStaleEntries(maxAge: number): Promise<void> {
        const cutoff = Date.now() - maxAge;
        await this.kvStore
            .where('timestamp')
            .below(cutoff)
            .delete();
    }

    /**
     * Gets the database size statistics
     */
    async getStats(): Promise<{
        kvStoreCount: number;
        zsetStoreCount: number;
        syncQueueCount: number;
    }> {
        const [kvStoreCount, zsetStoreCount, syncQueueCount] = await Promise.all([
            this.kvStore.count(),
            this.zsetStore.count(),
            this.syncQueue.count()
        ]);

        return {
            kvStoreCount,
            zsetStoreCount,
            syncQueueCount
        };
    }

    /**
     * Ensures all tables are empty and the database is in a clean state
     */
    async reset(): Promise<void> {
        await this.transaction('rw',
            this.kvStore,
            this.zsetStore,
            this.syncQueue,
            async () => {
                await Promise.all([
                    this.kvStore.clear(),
                    this.zsetStore.clear(),
                    this.syncQueue.clear()
                ]);
            }
        );
    }

    /**
     * Check database health and returns status
     */
    async healthCheck(): Promise<{
        isHealthy: boolean;
        tables: {
            kvStore: boolean;
            zsetStore: boolean;
            syncQueue: boolean;
        };
    }> {
        try {
            // Try to access each table
            await Promise.all([
                this.kvStore.count(),
                this.zsetStore.count(),
                this.syncQueue.count()
            ]);

            return {
                isHealthy: true,
                tables: {
                    kvStore: true,
                    zsetStore: true,
                    syncQueue: true
                }
            };
        } catch (error) {
            console.error('Database health check failed:', error);
            return {
                isHealthy: false,
                tables: {
                    kvStore: false,
                    zsetStore: false,
                    syncQueue: false
                }
            };
        }
    }

    /**
     * Gets the total size of data stored in the database (approximate)
     */
    async getStorageSize(): Promise<{
        kvStoreSize: number;
        zsetStoreSize: number;
        syncQueueSize: number;
        totalSize: number;
    }> {
        const [kvStore, zsetStore, syncQueue] = await Promise.all([
            this.kvStore.toArray(),
            this.zsetStore.toArray(),
            this.syncQueue.toArray()
        ]);

        const getSize = (obj: any): number => {
            const str = JSON.stringify(obj);
            // Approximate size in bytes (2 bytes per character in UTF-16)
            return str.length * 2;
        };

        const kvStoreSize = kvStore.reduce((acc, item) => acc + getSize(item), 0);
        const zsetStoreSize = zsetStore.reduce((acc, item) => acc + getSize(item), 0);
        const syncQueueSize = syncQueue.reduce((acc, item) => acc + getSize(item), 0);

        return {
            kvStoreSize,
            zsetStoreSize,
            syncQueueSize,
            totalSize: kvStoreSize + zsetStoreSize + syncQueueSize
        };
    }
}

// Create and export a single instance
export const localDatabase = new LocalDatabase();